/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable dot-notation */
/* eslint-disable react-native/no-inline-styles */
/**
 * Cart Screen
 * 
 * Comprehensive cart and checkout screen handling:
 * - Multi-canteen cart support with separate totals
 * - Multiple payment methods (Cash, UPI, Card, Online Payment Gateway)
 * - Split payment support (partial amounts across multiple methods)
 * - Order notes per canteen
 * - Order printing (Bluetooth for mobile, thermal for Electron, HTTP for web)
 * - PayU payment gateway integration for online payments
 * - Real-time cart calculations with taxes and subsidies
 * 
 * @component
 * @param {Object} navigation - React Navigation object
 */
import React, { useEffect, useRef, useState } from 'react';
import { InteractionManager, FlatList, Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Card, Dialog, Divider, Portal, Text, TextInput } from 'react-native-paper';
import { useDispatch, useSelector } from 'react-redux';
import Item from '../components/Canteen/Item';
import CartNotes from '../components/Canteen/CartNotes';
import { doGet, doPost } from '../services/http';
import { BASE_URL, delay, errorHandler, getCartCanteens } from '../services/Utils';
import { hideLoader, showAlert, showLoader, showToast } from '../store/info.slice';
import { resetCart, resetCartSubsidy } from '../store/cart.slice';
import CashCounterPaymentMethodsModal from '../components/Cart/CashCounterPaymentMethodsModal';
import { fontConfig, safeAreaNavigationBar } from '../theme/theme';
import SplitPaymentDIalog from '../components/Cart/SplitPaymentDIalog';
import * as BluetoothPrintService from '../services/BluetoothPrintService';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { printOrderData } from '../services/PrintService';
import { useFormik } from 'formik/dist';
import * as yup from 'yup';
import { sha512 } from 'js-sha512';

const CARD_ROUNDNESS = 1;
const CARD_ELEVATION = 0;

/**
 * Main Cart screen component. Handles cart display, order creation, and payment flows.
 * @param {object} navigation - Navigation prop for screen navigation
 * @param {boolean} isTablet - Flag to indicate if device is a tablet
 */
function Cart({ navigation }) {
    const dispatch = useDispatch();
    const isDataLoaded = useRef(null);
    const insets = useSafeAreaInsets();
    const data = ['Canteens'];
    const [interactionCompleted, setIntersectionCompleted] = useState(false);
    const isTablet = useSelector((state) => state.info?.isTablet);
    const [orderRefNo, setOrderRefNo] = useState(null);
    const cc_payment_methods = useSelector((state) => state.info?.cc_payment_methods);
    const customer = useSelector((state) => state.customer?.customer);
    const enable_print = (customer?.customer_settings?.enable_order_print);
    const _cartCanteens = useSelector((state) => state.cart?.canteens);
    const cartCanteens = Object.keys(_cartCanteens);
    const cartInfo = useSelector((state) => state.cart?.cartInfo);
    const totalCartItems = useSelector((state) => state.cart?.totalCartItems);
    const totalCartGrandTotal = useSelector((state) => state.cart?.totalCartGrandTotal);
    const currency_symbol = useSelector((state) => state.info?.info?.currency_symbol);
    const grand_total = totalCartGrandTotal;
    const [isCCModalVisisble, setCCModalVisible] = useState(false);
    const [visibleSplitPaymentDialog, setVisibleSplitPaymentDialog] = useState(false);
    const [visibleCCDialog, setVisibleCCDialog] = useState(false);
    const hideCCDialog = () => setVisibleCCDialog(false);
    const [cc_payment_method, setCCPaymentMethod] = useState(null);
    const CouponSchema = yup.object().shape({
        coupon_code: yup.string().min(3, 'Coupon Code must be at least 3 characters').required('Please enter coupon code'),
    });
    const CCFormik = useFormik({
        initialValues: {
            coupon_code: '',
        },
        validationSchema: CouponSchema,
        onSubmit: async (val) => {
            try {
                CCFormik.setSubmitting(false);
                setVisibleCCDialog(false);
                await delay(500);
                createOrder({ cc_pm_id: cc_payment_method?.cc_pm_id, coupon_code: val?.coupon_code });
            } catch (error) {
                console.error(error);
            }
        },
    });
    /**
     * Hides the split payment dialog modal.
     */
    const hideSplitPaymentDialog = () => {
        setVisibleSplitPaymentDialog(false);
    };

    /**
     * Fetches a new order reference number from the server.
     */
    const gerOrderRefNo = () => {
        doGet('orders/ref-no').then((response) => {
            setOrderRefNo(response.data?.order_ref_no || null);
        }).catch(errorHandler);
    };

    useEffect(() => {
        const task = InteractionManager.runAfterInteractions(async () => {
            setIntersectionCompleted(true);
        });
        return () => {
            dispatch(resetCartSubsidy());
            task.cancel();
        };
    }, []);

    useEffect(() => {
        if (interactionCompleted && !!customer) {
            if (totalCartItems === 0) {
                setOrderRefNo(null);
            } else if (!orderRefNo && totalCartItems > 0) {
                gerOrderRefNo();
            }
        }
    }, [interactionCompleted, customer, orderRefNo, totalCartItems]);

    useEffect(() => {
        if (interactionCompleted && customer) {
            isDataLoaded.current = true;
        }
    }, [interactionCompleted, customer]);

    /**
     * Opens the payment gateways modal.
     */
    const openPaymentGatewaysModal = () => {
        if (cc_payment_methods.length > 0) {
            if (cc_payment_methods.length === 1) {
                onCCPaymentMethodSelect(cc_payment_methods[0]);
            } else {
                setCCModalVisible(true);
            }
        } else {
            dispatch(showAlert('Payment Method is not found'));
        }
    };

    /**
     * Closes the payment method modal.
     */
    const closeCCPaymentMethodModal = () => {
        setCCModalVisible(false);
    };

    /**
     * Handles selection of a payment method from the modal.
     * @param {object} paymentInfo - Selected payment method info
     */
    const onCCPaymentMethodSelect = async (paymentInfo) => {
        try {
            closeCCPaymentMethodModal();
            await delay(500);
            setCCPaymentMethod(paymentInfo);
            const { cc_pm_alias, cc_pm_id, cc_payment_notes } = paymentInfo;
            const baseOrderParams = {
                cc_pm_id,
                cc_payment_notes: cc_payment_notes || null,
            };

            switch (cc_pm_alias) {
                case 'coupon':
                    setVisibleCCDialog(true);
                    break;
                case 'split_payment':
                    setVisibleSplitPaymentDialog(true);
                    break;

                default:
                    createOrder(baseOrderParams);
                    break;
            }
        } catch (error) {
            console.error(error);
        }
    };

    /**
     * Handles split payment data received from the dialog.
     * @param {string} split_payment - Notes or info for split payment
     */
    const onSplitPaymentReceived = async (split_payment) => {
        try {
            setVisibleSplitPaymentDialog(false);
            await delay(500);
            const selectedItem = cc_payment_methods.find(item => item.cc_pm_alias === 'split_payment');
            if (!selectedItem) {
                dispatch(showToast('Please Select Payment method'));
            } else {
                createOrder({ cc_pm_id: selectedItem?.cc_pm_id, cc_payment_notes: split_payment || null, coupon_code: null });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getCartHash = (__data) => {
        return sha512(customer?.customer_ref_id + '|' + 'create_order' + '|' + (__data.grand_total).toString() + '|' + orderRefNo);
    };

    /**
     * Creates a new order and triggers printing if enabled.
     * @param {object} params - Order parameters (payment method, notes, coupon)
     */
    const createOrder = async (params) => {
        try {
            if (!orderRefNo) {
                dispatch(showAlert('Order Reference number is missing. Please try again'));
                return;
            }
            const order = {
                canteens: getCartCanteens(),
                cartInfo,
                grand_total: totalCartGrandTotal,
                order_ref_no: orderRefNo,
                callback_url: `${BASE_URL()}/cash-counter`,
            };
            if (params?.cpg_id) {
                order['cpg_id'] = params?.cpg_id;
            }
            if (params?.cpg_mode) {
                order['cpg_mode'] = params?.cpg_mode;
            }
            if (params?.payment_id) {
                order['payment_id'] = params?.payment_id;
            }
            if (params?.txn_id) {
                order['txn_id'] = params?.txn_id;
            }
            if (params?.cart_id) {
                order['cart_id'] = params?.cart_id;
            }
            if (params?.cc_pm_id) {
                order['cc_pm_id'] = params?.cc_pm_id;
                if (params?.coupon_code) {
                    order['coupon_code'] = params?.coupon_code || null;
                }
                if (params?.cc_payment_notes) {
                    order['cc_payment_notes'] = params?.cc_payment_notes || null;
                }
            }
            order['hash'] = getCartHash(order);
            console.log('order', order);
            dispatch(showLoader());
            const response = await doPost('orders', order);
            dispatch(resetCart());
            dispatch(hideLoader());
            await delay(500);
            dispatch(showToast({ message: response.data.message, type: 'success' }));
            if (enable_print && response?.data?.orders && response?.data?.orders?.length > 0) {
                if (Platform.OS === 'web') {
                    for (const element of response?.data?.orders) {
                        console.log(element);
                        await printOrderData(element?.print_data);
                    }
                } else if (Platform.OS === 'android') {
                    for (const element of response?.data?.orders) {
                        console.log(element);
                        await BluetoothPrintService.printOrderData(element?.print_data);
                    }
                }
            }
            if (!isTablet) {
                navigation.replace('Home');
            }
        } catch (error) {
            console.error(error);
            errorHandler(error);
        }
    };

    /**
     * Renders a single canteen's cart section with items and pricing.
     * @param {object} param0 - Object with canteen id as item
     */
    const renderCartCanteen = ({ item }) => {
        const settings = _cartCanteens?.[item]['canteen']?.settings || {};
        return (<React.Fragment>
            {cartInfo?.[item]?.cart_len > 0 &&
                <Card elevation={CARD_ELEVATION} style={{ backgroundColor: '#ffffff', marginVertical: 4 }} theme={{ roundness: CARD_ROUNDNESS }}>
                    <Card.Title
                        title={_cartCanteens?.[item]['canteen']?.canteen_name}
                        titleStyle={{ textTransform: 'capitalize' }}
                        // subtitle={(<View>
                        //     {!!_cartCanteens?.[item]['canteen']?.delivery_date && <Text>Delivery Date: {moment(_cartCanteens?.[item]['canteen']?.delivery_date).format('LL')}</Text>}
                        //     <Text>Item(s)</Text>
                        // </View>)}
                        titleVariant="titleMedium"
                        subtitleVariant="titleSmall"
                    />
                    <Divider style={{ backgroundColor: '#dddddd' }} />
                    <Card.Content style={{ paddingHorizontal: 0 }}>
                        {_cartCanteens?.[item]['cart'].map((cartItem) => {
                            return (
                                <React.Fragment>
                                    <Item canteen={_cartCanteens?.[item]['canteen']} item={cartItem} isCartItem={true} key={item?.item_id} />
                                    <Divider style={{ marginHorizontal: 16 }} />
                                </React.Fragment>
                            );
                        })}
                    </Card.Content>
                    <React.Fragment>
                        {!customer?.company_settings?.disable_order_note && (<React.Fragment>
                            <Divider style={{ backgroundColor: '#dddddd', marginTop: 4 }} />
                            <CartNotes canteen_id={item} />
                            <Divider style={{ backgroundColor: '#dddddd' }} />
                        </React.Fragment>)}
                        <Card.Content style={{ paddingTop: 4 }}>
                            {/* <Text variant="titleSmall">Pricing Information</Text> */}
                            <React.Fragment>
                                {!settings?.hide_sub_total &&
                                    <View style={styles.billInfo}>
                                        <Text>Items Total</Text>
                                        <Text variant="titleSmall">{currency_symbol}{cartInfo[item]?.sub_total}</Text>
                                    </View>
                                }
                                {cartInfo[item]?.sgst > 0 &&
                                    <View style={styles.billInfo}>
                                        <Text>SGST({cartInfo[item]?.sgst_percentage}%)</Text>
                                        <Text variant="titleSmall">{currency_symbol}{cartInfo[item]?.sgst}</Text>
                                    </View>
                                }
                                {cartInfo[item]?.cgst > 0 &&
                                    <View style={styles.billInfo}>
                                        <Text>CGST({cartInfo[item]?.cgst_percentage}%)</Text>
                                        <Text variant="titleSmall">{currency_symbol}{cartInfo[item]?.cgst}</Text>
                                    </View>
                                }
                                {cartInfo[item]?.igst > 0 &&
                                    <View style={styles.billInfo}>
                                        <Text>IGST({cartInfo[item]?.igst_percentage}%)</Text>
                                        <Text variant="titleSmall">{currency_symbol}{cartInfo[item]?.igst}</Text>
                                    </View>
                                }
                            </React.Fragment>
                            <View style={styles.billInfo}>
                                <Text variant="titleMedium">Total</Text>
                                <Text variant="titleMedium">
                                    {currency_symbol}{cartInfo[item]?.total}
                                </Text>
                            </View>
                        </Card.Content>
                    </React.Fragment>
                </Card>
            }
        </React.Fragment>);
    };

    /**
     * Renders the FlatList of canteens in the cart.
     */
    const renderItem = () => (
        <FlatList
            data={cartCanteens}
            renderItem={renderCartCanteen}
        />
    );

    if (totalCartItems === 0) {
        return (
            <SafeAreaView style={styles.container}>
                {interactionCompleted &&
                    <View style={[styles.mainContainer, { justifyContent: 'center', alignItems: 'center' }]}>
                        <Text variant="titleMedium">No items in cart</Text>
                    </View>
                }
            </SafeAreaView>
        );
    }

    if (!orderRefNo) {
        return (
            <SafeAreaView style={styles.container}>
                {interactionCompleted &&
                    <View style={[styles.mainContainer, { justifyContent: 'center', alignItems: 'center' }]}>
                        <Text variant="titleMedium">Loading...</Text>
                    </View>
                }
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView edges={['bottom']} style={styles.container}>
            {interactionCompleted &&
                <React.Fragment>
                    {totalCartItems > 0 ?
                        <React.Fragment>
                            <View style={styles.mainContainer}>
                                <FlatList
                                    data={data}
                                    renderItem={renderItem}
                                />
                            </View>
                            <View style={[styles.checkoutFooterContainer, isTablet && styles.noShadow, safeAreaNavigationBar(insets)]}>
                                <View>
                                    <Text variant="titleSmall">{currency_symbol}{grand_total}</Text>
                                    <Text variant="titleSmall">{totalCartItems} {totalCartItems === 1 ? 'item' : 'items'}</Text>
                                </View>
                                <View>
                                    <Button
                                        onPress={openPaymentGatewaysModal}
                                        mode="contained">
                                        Place Order
                                    </Button>
                                </View>
                            </View>
                            {cc_payment_methods.length > 0 &&
                                <React.Fragment>
                                    <CashCounterPaymentMethodsModal amount={totalCartGrandTotal} payment_methods={cc_payment_methods} visible={isCCModalVisisble} onCCPaymentMethodModalClose={closeCCPaymentMethodModal} onCCPaymentMethodSelect={onCCPaymentMethodSelect} />
                                    <Portal>
                                        <SplitPaymentDIalog hideDialog={hideSplitPaymentDialog} visible={visibleSplitPaymentDialog} amount={totalCartGrandTotal} onSplitPaymentReceived={onSplitPaymentReceived} />
                                        <Dialog visible={visibleCCDialog} onDismiss={hideCCDialog}>
                                            <Dialog.Title style={{ ...fontConfig.titleSmall }}>Enter Coupon Code</Dialog.Title>
                                            <Dialog.Content>
                                                <TextInput
                                                    label="Coupon Code"
                                                    value={CCFormik.values.coupon_code}
                                                    onBlur={CCFormik.handleBlur('coupon_code')}
                                                    touched={CCFormik.touched.coupon_code}
                                                    onChangeText={CCFormik.handleChange('coupon_code')}
                                                />
                                                {CCFormik.touched.coupon_code && CCFormik.errors.coupon_code && (
                                                    <Text style={styles.errorText}>{CCFormik.errors.coupon_code}</Text>
                                                )}
                                            </Dialog.Content>
                                            <Dialog.Actions>
                                                <Button onPress={hideCCDialog}>Cancel</Button>
                                                <Button onPress={CCFormik.handleSubmit} disabled={CCFormik.isSubmitting}>{CCFormik.isSubmitting ? 'Please Wait...' : 'Submit'}</Button>
                                            </Dialog.Actions>
                                        </Dialog>
                                    </Portal>
                                </React.Fragment>
                            }
                        </React.Fragment>
                        : (<View style={[styles.mainContainer, { justifyContent: 'center', alignItems: 'center' }]}>
                            <Text variant="titleMedium">No items in cart</Text>
                            {!isTablet &&
                                <Button
                                    onPress={() => navigation.replace('Home')}
                                    mode="contained">
                                    Go to Home
                                </Button>
                            }
                        </View>)}
                </React.Fragment>
            }
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f1f1f3',
    },
    mainContainer: {
        flex: 1,
        margin: 4,
    },
    checkoutFooterContainer: {
        margin: 0,
        padding: 8,
        paddingHorizontal: 16,
        flexDirection: 'row',
        backgroundColor: '#ffffff',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: Platform.OS === 'web' ? 'sticky' : undefined,
        bottom: 0,
        width: '100%',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,

        elevation: 5,
    },
    noShadow: {
        elevation: 0, // Android
        shadowColor: 'transparent', // iOS
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
    },
    billInfo: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    modalConatiner: {
        margin: 0,
        // flex: 1,
        justifyContent: 'flex-end',
    },
    modalView: {
        // flexGrow: 1,
        minHeight: '20%',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
    },
    closeIcon: {
        position: 'absolute',
        top: 10, // Adjust as needed
        right: 10, // Adjust as needed
        backgroundColor: '#FFF',
        borderRadius: 20, // Makes it circular if height and width are equal
        width: 32,
        height: 32,
        justifyContent: 'center',
        alignItems: 'center',
    },
    errorText: {
        color: 'red',
        fontSize: 12,
        marginTop: 2,
    },
});

export default Cart;
